import { NextRequest, NextResponse } from 'next/server';
import { COOKIE_NAMES, getBaseCookieOptions, serializeUserCookie } from '../lib/cookies';

export interface InsforgeMiddlewareConfig {
  /**
   * Base URL of your Insforge backend
   * @example 'https://your-backend.com' or 'http://localhost:3001'
   */
  baseUrl: string;

  /**
   * Routes that are public and don't require authentication
   * @default ['/']
   * @example ['/sign-in', '/sign-up', '/', '/about']
   */
  publicRoutes?: string[];

  /**
   * Local route path for sign-in (can be customized to any path like '/login')
   * When using built-in auth, this will redirect to backend's `/auth/sign-in`
   * @default '/sign-in'
   */
  signInUrl?: string;

  /**
   * Local route path for sign-up (can be customized to any path like '/register')
   * When using built-in auth, this will redirect to backend's `/auth/sign-up`
   * @default '/sign-up'
   */
  signUpUrl?: string;

  /**
   * Local route path for forgot password
   * When using built-in auth, this will redirect to backend's `/auth/forgot-password`
   * @default '/forgot-password'
   */
  forgotPasswordUrl?: string;

  /**
   * URL to redirect to after successful authentication
   * When user completes sign-in/sign-up, they will be redirected to this URL with token in URL
   * @default '/'
   */
  afterSignInUrl?: string;

  /**
   * Whether to use built-in authentication pages hosted on the backend
   * - When true: redirects to backend's `/auth/sign-in` and `/auth/sign-up` pages
   * - When false: redirects to local sign-in/sign-up pages (you provide your own components)
   * @default true
   */
  useBuiltInAuth?: boolean;
}

/**
 * Creates Next.js middleware for protecting routes with Insforge authentication.
 *
 * This middleware provides lightweight route protection by:
 * - Detecting and storing auth tokens from URL parameters (after backend redirect)
 * - Checking for auth token presence in cookies
 * - Redirecting unauthenticated users to sign-in page
 * - Allowing public routes to be accessed without authentication
 * - Mapping local auth routes to backend's fixed paths when using built-in auth
 *
 * **How Authentication Flow Works:**
 * 1. User visits protected route → Middleware checks for token
 * 2. No token → Redirects to sign-in (backend or local)
 * 3. User accesses auth page (e.g., /sign-in) → Redirects to backend with afterSignInUrl as redirect target
 * 4. After sign-in → Backend redirects to `yourapp.com/afterSignInUrl?access_token=xxx&user_id=xxx...`
 * 5. Middleware detects `access_token` in URL → Stores in HTTP-only cookie → Cleans URL → Allows access
 * 6. SDK also detects token from URL → Stores in localStorage → Updates auth state
 *
 * **Important Notes:**
 * - This middleware only checks if a token exists, it doesn't validate it
 * - Tokens from URL are automatically extracted and stored in cookies
 * - When `useBuiltInAuth: true`, local routes map to backend's fixed auth paths
 * - You can customize local route paths (e.g., `/login`) while backend paths remain fixed
 * - After successful auth, users are redirected to `afterSignInUrl` (default: `/`), not back to the auth page
 *
 * @param config - Middleware configuration
 * @returns Next.js middleware function
 *
 * @example
 * ```ts
 * // middleware.ts - Using built-in auth
 * import { InsforgeMiddleware } from '@insforge/nextjs/middleware';
 *
 * export default InsforgeMiddleware({
 *   baseUrl: process.env.INSFORGE_BASE_URL!,
 *   publicRoutes: ['/', '/about'],
 *   afterSignInUrl: '/', // Redirect here after successful auth
 *   useBuiltInAuth: true,
 * });
 *
 * export const config = {
 *   matcher: ['/((?!_next|api|.*\\..*).*)'],
 * };
 * ```
 *
 * @example
 * ```ts
 * // middleware.ts - Custom local auth pages with custom paths
 * import { InsforgeMiddleware } from '@insforge/nextjs/middleware';
 *
 * export default InsforgeMiddleware({
 *   baseUrl: process.env.INSFORGE_BASE_URL!,
 *   publicRoutes: ['/login', '/register', '/', '/about'],
 *   signInUrl: '/login',
 *   signUpUrl: '/register',
 *   afterSignInUrl: '/dashboard',
 *   useBuiltInAuth: false,
 * });
 *
 * export const config = {
 *   matcher: ['/((?!_next|api|.*\\..*).*)'],
 * };
 * ```
 *
 * @example
 * ```ts
 * // middleware.ts - Built-in auth with custom auth route paths
 * import { InsforgeMiddleware } from '@insforge/nextjs/middleware';
 *
 * export default InsforgeMiddleware({
 *   baseUrl: 'https://your-backend.com',
 *   signInUrl: '/login',
 *   signUpUrl: '/register',
 *   forgotPasswordUrl: '/forgot',
 *   afterSignInUrl: '/dashboard',
 *   useBuiltInAuth: true,
 * });
 * ```
 */
export const InsforgeMiddleware = (config: InsforgeMiddlewareConfig) => {
  const {
    baseUrl,
    publicRoutes = ['/'],
    signInUrl = '/sign-in',
    signUpUrl = '/sign-up',
    forgotPasswordUrl = '/forgot-password',
    afterSignInUrl = '/',
    useBuiltInAuth = true,
  } = config;

  return function middleware(request: NextRequest) {
    const { pathname, searchParams } = request.nextUrl;

    // STEP 1: Check if URL contains access_token (from backend redirect after auth)
    const accessToken = searchParams.get('access_token');
    const userId = searchParams.get('user_id');
    const email = searchParams.get('email');
    const name = searchParams.get('name');

    if (accessToken && userId && email) {
      // Token detected in URL - store it in HTTP-only cookies
      const response = NextResponse.next();
      const cookieOptions = getBaseCookieOptions();

      // Set session cookie (access token)
      response.cookies.set(COOKIE_NAMES.SESSION, accessToken, cookieOptions);

      // Set user identity cookie (basic user info for SSR)
      response.cookies.set(
        COOKIE_NAMES.USER,
        serializeUserCookie({ id: userId, email, profile: { name: name || undefined } }),
        cookieOptions
      );

      return response;
    }

    // STEP 2: Handle built-in auth redirects for authentication pages
    // Map local auth routes to backend's fixed auth pages
    if (useBuiltInAuth) {
      const authRouteMapping: Record<string, string> = {
        [signInUrl]: '/auth/sign-in',
        [signUpUrl]: '/auth/sign-up',
        [forgotPasswordUrl]: '/auth/forgot-password',
      };

      const backendAuthPath = authRouteMapping[pathname];
      if (backendAuthPath) {
        // Redirect to afterSignInUrl after successful authentication
        const redirectUrl = new URL(afterSignInUrl, request.url).toString();
        const backendAuthUrl = new URL(backendAuthPath, baseUrl);
        backendAuthUrl.searchParams.set('redirect', redirectUrl);
        return NextResponse.redirect(backendAuthUrl.toString());
      }
    }

    // STEP 3: Check if route is public
    const isPublicRoute = publicRoutes.some((route) => {
      if (route.endsWith('*')) {
        // Wildcard route: /admin/* matches /admin/anything
        return pathname.startsWith(route.slice(0, -1));
      }
      // Exact match or starts with route path
      return pathname === route || pathname.startsWith(route + '/');
    });

    if (isPublicRoute) {
      return NextResponse.next();
    }

    // STEP 4: Check for authentication token in cookies
    const token = request.cookies.get(COOKIE_NAMES.SESSION)?.value;

    if (!token) {
      // No token found, redirect to sign-in
      if (useBuiltInAuth) {
        // Built-in auth: redirect to backend's /auth/sign-in page
        // Backend will redirect back to current URL with token
        const backendSignInUrl = new URL('/auth/sign-in', baseUrl);
        backendSignInUrl.searchParams.set('redirect', request.url);
        return NextResponse.redirect(backendSignInUrl);
      } else {
        // Custom auth: redirect to local sign-in page
        const localSignInUrl = new URL(signInUrl, request.url);
        localSignInUrl.searchParams.set('redirect', pathname);
        return NextResponse.redirect(localSignInUrl);
      }
    }

    // Token exists in cookie, allow request to continue
    return NextResponse.next();
  };
};
